import { useEffect } from "react";

const maxAttempts = 20;
const retryDelayMs = 150;

// Walks up to the nearest scrollable ancestor (the sidebar's scroll container),
// so we measure "in view" against the right box rather than the whole window.
const getScrollParent = (el) => {
  let node = el.parentElement;
  while (node) {
    const { overflowY } = window.getComputedStyle(node);
    const scrollable = overflowY === "auto" || overflowY === "scroll";
    if (scrollable && node.scrollHeight > node.clientHeight) return node;
    node = node.parentElement;
  }
  return null;
};

const isInView = (el, container) => {
  const rect = el.getBoundingClientRect();
  const bounds = container
    ? container.getBoundingClientRect()
    : {
        top: 0,
        bottom: window.innerHeight || document.documentElement.clientHeight,
      };
  return rect.top >= bounds.top && rect.bottom <= bounds.bottom;
};

// Scrolls the referenced element into view once `active` becomes true.
// On page load the sidebar's scroll container isn't scrollable yet at the point
// the tree finishes expanding — calling scrollIntoView() then is a silent no-op.
// Rather than guess the exact moment it becomes scrollable, we re-issue the
// scroll and re-check whether it landed, until it does (or we give up).
export const useScrollIntoViewOnSelect = (ref, active) => {
  useEffect(() => {
    if (!active) return;

    let timeoutId;
    let attempts = 0;

    const tryScroll = () => {
      attempts += 1;
      const el = ref.current;
      if (!el) {
        if (attempts < maxAttempts) {
          timeoutId = setTimeout(tryScroll, retryDelayMs);
        }
        return;
      }

      if (isInView(el, getScrollParent(el))) return;

      // Only the first attempt animates; retries use "auto" so a container that
      // settles over several ticks doesn't produce a visible stutter.
      el.scrollIntoView({
        behavior: attempts === 1 ? "smooth" : "auto",
        block: "center",
        inline: "nearest",
      });

      if (attempts < maxAttempts) {
        timeoutId = setTimeout(tryScroll, retryDelayMs);
      }
    };

    timeoutId = setTimeout(tryScroll, retryDelayMs);

    return () => clearTimeout(timeoutId);
  }, [active, ref]);
};
